import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Icon,
  Text,
  Alert,
  AlertIcon,
  useColorModeValue,
  HStack,
  VStack,
  Divider,
  Flex,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiCreditCard,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiUpload,
  FiEye,
  FiDownload,
  FiRefreshCw,
  FiPlus,
} from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import { api } from '../../services/api';

interface FinancialSummary {
  total_income: number;
  total_expenses: number;
  net_cash_flow: number;
  budget_utilization: number;
  sponsorship_value: number;
  active_sponsors: number;
  pending_expenses: number;
  over_budget_count: number;
}

interface BudgetCategory {
  name: string;
  budgeted: number;
  spent: number;
  remaining: number;
  utilization: number;
  status: 'ok' | 'warning' | 'critical';
}

interface BudgetDashboard {
  total_budget: number;
  used_budget: number;
  remaining_budget: number;
  utilization: number;
  budget_categories: BudgetCategory[];
  alerts: any[];
}

interface SponsorshipDashboard {
  total_value: number;
  received_this_year: number;
  pending_this_year: number;
  active_count: number;
  expiring_count: number;
  contract_types: any[];
  payment_status: any[];
}

interface ExpenseCategory {
  name: string;
  amount: number;
  count: number;
  percent: number;
}

interface ExpenseDashboard {
  total_expenses: number;
  approved_expenses: number;
  pending_expenses: number;
  rejected_expenses: number;
  with_receipt_rate: number;
  categories: ExpenseCategory[];
  monthly_trend: any[];
}

interface CashFlowItem {
  month: string;
  income: number;
  expenses: number;
  net_flow: number;
  cumulative: number;
}

interface UpcomingPayment {
  type: string;
  description: string;
  amount: number;
  due_date: string;
  status: string;
  sponsor_name?: string;
}

interface RecentActivity {
  type: string;
  description: string;
  amount?: number;
  date: string;
  user?: string;
}

interface FinancialDashboard {
  budget_summary: {
    total_budget: number;
    total_spend: number;
    remaining_budget: number;
    budget_count: number;
    over_budget_count: number;
  };
  expense_summary: {
    total_expenses: number;
    pending_expenses: number;
    approved_expenses: number;
    this_month_expenses: number;
    expense_count: number;
    pending_count: number;
  };
  sponsorship_summary: {
    total_sponsors: number;
    active_sponsors: number;
    total_value: number;
    received_this_year: number;
  };
  recent_expenses: any[];
  recent_sponsorships: any[];
}

const FinancialDashboard: React.FC = () => {
  const [dashboard, setDashboard] = useState<FinancialDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const toast = useToast();
  const navigate = useNavigate();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    fetchDashboard();
  }, [selectedYear]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/financial/dashboard?year=${selectedYear}`);
      setDashboard(response.data);
    } catch (error) {
      toast({
        title: 'Chyba při načítání dat',
        description: 'Nepodařilo se načíst finanční data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: 'CZK',
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ok':
        return 'green';
      case 'warning':
        return 'yellow';
      case 'critical':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getProgressColor = (utilization: number) => {
    if (utilization >= 100) return 'red';
    if (utilization >= 80) return 'yellow';
    return 'green';
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Načítám finanční data...</Text>
        </VStack>
      </Flex>
    );
  }

  if (!dashboard) {
    return (
      <Alert status="error">
        <AlertIcon />
        Nepodařilo se načíst finanční data
      </Alert>
    );
  }

  return (
    <AdminLayout>
      <VStack spacing={8} align="stretch" maxW="1200px" mx="auto" p={8}>
        <Box>
          <Flex justify="space-between" align="center" mb={4}>
            <Box>
              <Heading size="lg" mb={2}>Finanční přehled</Heading>
              <HStack spacing={4}>
                <Text color="gray.600">Rok:</Text>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  {[selectedYear - 2, selectedYear - 1, selectedYear, selectedYear + 1].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </HStack>
            </Box>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={() => navigate('/admin/invoices')}
            >
              Nová faktura
            </Button>
          </Flex>
        </Box>

      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel>Cash Flow</StatLabel>
              <StatNumber>
                {formatCurrency(dashboard.sponsorship_summary.received_this_year - dashboard.expense_summary.total_expenses)}
              </StatNumber>
              <StatHelpText>
                <StatArrow
                  type={(dashboard.sponsorship_summary.received_this_year - dashboard.expense_summary.total_expenses) >= 0 ? 'increase' : 'decrease'}
                />
                {(dashboard.sponsorship_summary.received_this_year - dashboard.expense_summary.total_expenses) >= 0 ? 'Příjem' : 'Výdaj'}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel>Celkové příjmy</StatLabel>
              <StatNumber>{formatCurrency(dashboard.sponsorship_summary.received_this_year)}</StatNumber>
              <StatHelpText>
                <Icon as={FiTrendingUp} color="green.500" mr={2} />
                Od sponzorů
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel>Celkové výdaje</StatLabel>
              <StatNumber>{formatCurrency(dashboard.expense_summary.total_expenses)}</StatNumber>
              <StatHelpText>
                <Icon as={FiTrendingDown} color="red.500" mr={2} />
                {dashboard.expense_summary.pending_count > 0 && `${dashboard.expense_summary.pending_count} čeká na schválení`}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel>Využití rozpočtu</StatLabel>
              <StatNumber>{dashboard.budget_summary.total_budget > 0 ? ((dashboard.budget_summary.total_spend / dashboard.budget_summary.total_budget) * 100).toFixed(1) : '0'}%</StatNumber>
              <Progress
                value={dashboard.budget_summary.total_budget > 0 ? Math.min((dashboard.budget_summary.total_spend / dashboard.budget_summary.total_budget) * 100, 100) : 0}
                colorScheme={getProgressColor(dashboard.budget_summary.total_budget > 0 ? (dashboard.budget_summary.total_spend / dashboard.budget_summary.total_budget) * 100 : 0)}
                mt={2}
              />
              {dashboard.budget_summary.over_budget_count > 0 && (
                <StatHelpText>
                  <Icon as={FiAlertTriangle} color="red.500" mr={2} />
                  {dashboard.budget_summary.over_budget_count} rozpočtů překročeno
                </StatHelpText>
              )}
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Main Content Tabs */}
      <Tabs>
        <TabList>
          <Tab>Rozpočty</Tab>
          <Tab>Sponzoring</Tab>
          <Tab>Výdaje</Tab>
          <Tab>Cash Flow</Tab>
        </TabList>

        <TabPanels>
          {/* Budget Overview */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Celkový rozpočet</StatLabel>
                      <StatNumber>{formatCurrency(dashboard.budget_summary.total_budget)}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>

                <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Využito</StatLabel>
                      <StatNumber>{formatCurrency(dashboard.budget_summary.total_spend)}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>

                <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Zbývá</StatLabel>
                      <StatNumber>{formatCurrency(dashboard.budget_summary.remaining_budget)}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
              </SimpleGrid>

              <Alert status="info">
                <AlertIcon />
                Detailní rozpočty a kategorie budou dostupné v další verzi.
              </Alert>
            </VStack>
          </TabPanel>

          {/* Sponsorship Overview */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Aktivní sponzoři</StatLabel>
                      <StatNumber>{dashboard.sponsorship_summary.active_sponsors}</StatNumber>
                      <StatHelpText>
                        <Icon as={FiCheckCircle} color="green.500" mr={2} />
                        Celková hodnota: {formatCurrency(dashboard.sponsorship_summary.total_value)}
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>

                <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Přijato tento rok</StatLabel>
                      <StatNumber>{formatCurrency(dashboard.sponsorship_summary.received_this_year)}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>

                <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Celkem sponzorů</StatLabel>
                      <StatNumber>{dashboard.sponsorship_summary.total_sponsors}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>

                <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Aktivní smlouvy</StatLabel>
                      <StatNumber>{dashboard.sponsorship_summary.active_sponsors}</StatNumber>
                      <StatHelpText>
                        <Icon as={FiCheckCircle} color="green.500" mr={2} />
                        Momentálně aktivní
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </SimpleGrid>

              <Alert status="info">
                <AlertIcon />
                Detailní smlouvy a platby budou dostupné v další verzi.
              </Alert>
            </VStack>
          </TabPanel>

          {/* Expense Overview */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Celkem výdaje</StatLabel>
                      <StatNumber>{formatCurrency(dashboard.expense_summary.total_expenses)}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>

                <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Schváleno</StatLabel>
                      <StatNumber>{formatCurrency(dashboard.expense_summary.approved_expenses)}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>

                <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Čeká na schválení</StatLabel>
                      <StatNumber>{formatCurrency(dashboard.expense_summary.pending_expenses)}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>

                <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
                  <CardBody>
                    <Stat>
                      <StatLabel>Tento měsíc</StatLabel>
                      <StatNumber>{formatCurrency(dashboard.expense_summary.this_month_expenses)}</StatNumber>
                      <StatHelpText>
                        <Icon as={FiFileText} color="blue.500" mr={2} />
                        {dashboard.expense_summary.expense_count} celkem výdajů
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </SimpleGrid>

              <Alert status="info">
                <AlertIcon />
                Detailní kategorie a trendy budou dostupné v další verzi.
              </Alert>
            </VStack>
          </TabPanel>

          {/* Cash Flow */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Alert status="info">
                <AlertIcon />
                Měsíční cash flow přehled bude dostupný v další verzi.
              </Alert>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Recent Activity */}
      <Card bg={bgColor} borderWidth={1} borderColor={borderColor}>
        <CardHeader>
          <Heading size="md">Nedávná aktivita</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={3} align="stretch">
            {dashboard.recent_expenses?.slice(0, 5).map((expense, index) => (
              <HStack key={index} justify="space-between">
                <VStack align="start" spacing={1}>
                  <HStack>
                    <Icon as={FiCreditCard} color="red.500" />
                    <Text>{expense.title}</Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.600">
                    {new Date(expense.created_at).toLocaleDateString('cs-CZ')}
                  </Text>
                </VStack>
                <Text fontWeight="bold">
                  {formatCurrency(expense.total_amount)}
                </Text>
              </HStack>
            ))}
            {(!dashboard.recent_expenses || dashboard.recent_expenses.length === 0) && (
              <Text color="gray.500" textAlign="center">
                Žádná nedávná aktivita
              </Text>
            )}
          </VStack>
        </CardBody>
      </Card>
      </VStack>
    </AdminLayout>
  );
};

export default FinancialDashboard;
